import React, { useState, useEffect } from "react";
import { CloudUpload } from "lucide-react";

const FormInputFile = ({
  field,
  handleChange,
  error = null,
  imagePreview,
}) => {
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("Not selected file");
  const [mimeType, setMimeType] = useState("");
  const [ratioError, setRatioError] = useState("");

  const showPreview = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setMimeType(file.type);
      const isImageFile = file.type.startsWith("image/");

      if (isImageFile) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const acceptRatio = field.accept_ratio || null;
            if (acceptRatio) {
              const actualRatio = img.width / img.height;
              const expectedRatio = eval(acceptRatio);
              const tolerance = 0.05;

              if (Math.abs(actualRatio - expectedRatio) > tolerance) {
                setRatioError(`Invalid ratio. Expected ${acceptRatio}, got ${actualRatio.toFixed(2)}`);
                setPreview(null);
                setFileName("Not selected file");
                return;
              }
            }
            setPreview(e.target.result);
            setRatioError("");
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        // For non-image files (PDF, Word), set preview to a dummy string to trigger the preview mode
        // but ensure it doesn't start with data:image or http to fall into the icon logic
        setPreview("document-preview");
        setRatioError("");
      }
    } else {
      setPreview(null);
      setFileName("Not selected file");
      setRatioError("");
    }

    handleChange(event);
  };

  useEffect(() => {
    // Only initialize preview if it's currently null or if the imagePreview prop actually changed from outside
    if (imagePreview && typeof imagePreview === "string") {
      setPreview(imagePreview);
      // Extract filename from URL
      const parts = imagePreview.split("/");
      const name = parts[parts.length - 1];

      // Infer mimeType from extension for existing files
      const ext = name.toLowerCase().split('.').pop();
      if (ext === 'pdf') setMimeType('application/pdf');
      else if (['doc', 'docx'].includes(ext)) setMimeType('application/msword');
      else if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) setMimeType('image/' + ext);
    }
  }, [imagePreview]);

  const aligncontainer = field.align == 'center' ? 'mx-auto' : '';

  const isImage = typeof preview === "string" && (
    preview.startsWith("data:image/") ||
    (preview.startsWith("http") && !['.pdf', '.doc', '.docx'].some(ext => preview.toLowerCase().endsWith(ext)))
  );

  return (
    <div   className={`${field.fieldSize || "col-12"}`}>
      <div className={`upload-container w-100 ${aligncontainer}`}>
        <label
          htmlFor={field.name}
          className={`upload-header ${preview ? "has-image" : ""}`}
        >
          {!preview ? (
            <div className="upload-file">
              <CloudUpload size={70} strokeWidth={1} className="upload-icon" />
              <p>{field.label} Upload {field.required ? <span className="text-danger">*</span> : ""}</p>
              {field.accept_ratio && (
                <small>Required ratio: {field.accept_ratio}</small>
              )}
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center" title={fileName}>
              {isImage ? (
                <img src={preview} alt="Preview" className="upload-preview" />
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center border rounded p-3 bg-light" style={{ width: "120px", height: "120px" }}>
                  {mimeType === "application/pdf" ? (
                    <i className="bi bi-filetype-pdf text-danger fs-1" />
                  ) : (
                    <i className="bi bi-filetype-doc fs-1" style={{ color: "#103F91" }} />
                  )}
                </div>
              )}
              <span className="file-name-label small mt-2">{fileName}</span>
            </div>
          )}
        </label>

        <input
          id={field.name}
          type="file"
          name={field.name}
          accept={field.accept || "image/*"}
          onChange={showPreview}
          className="d-none"
          title={fileName}
          multiple={field.multiple ?? false}
          disabled={field.disabled ?? false}
        />

        {ratioError && <span className="text-danger mt-2">{ratioError}</span>}
        {error && <span className="text-danger mt-2">{error}</span>}
      </div>
    </div>
  );
};

export default FormInputFile;
